import React, { useMemo } from "react";
import { gql, useQuery } from "urql";
import { useParams, Link } from "wouter";
import { Briefcase, ChartLine, CircleDollarSign, FileText, Leaf, Settings } from "lucide-react";

import { PartyType, PermissionAssetEnum } from "../types.generated";
import { getMe } from "./SideNav";
import { ClientContextSelector } from "../contexts/ClientContextSelector";
import { IClient, ClientSelectorPlaceHolder } from "../../../components/src/contexts/ClientContext";
import { emptyObjectId } from "../../../common/src";

const GET_CLIENTS = gql`
    query {
        clients: parties(filter: { typeIn: [Client] }) {
            _id
            name
            dashName
        }
    }
`;

export function ClientPage(): React.ReactElement {
    const { clientDashName } = useParams<{ clientDashName: string }>();
    const [{ fetching, error, data }] = useQuery<{ clients?: IClient[] }>({ query: GET_CLIENTS });
    const [{ fetching: userLoading, error: userError, data: userData }] = useQuery({ query: getMe });

    const defaultClientName = "Select client";

    const [dashNameLookup] = useMemo(() => {
        const dashNameLookup: Record<string, IClient> = {};
        const nameLookup: Record<string, IClient> = {};
        const idLookup: Record<string, IClient> = {};
        if (data && data.clients) {
            for (const client of data.clients) {
                dashNameLookup[client.dashName] = client as IClient;
                nameLookup[client.name] = client as IClient;
                idLookup[client._id] = client as IClient;
            }
        }

        dashNameLookup[ClientSelectorPlaceHolder.dashName] = ClientSelectorPlaceHolder;
        nameLookup[ClientSelectorPlaceHolder.name] = ClientSelectorPlaceHolder;
        idLookup[ClientSelectorPlaceHolder._id] = ClientSelectorPlaceHolder;

        return [dashNameLookup, nameLookup, idLookup];
    }, [data]);

    if (error)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    if (userError) {
        console.log("userError: ", userError);
        return (
            <div>
                <p>user error:</p>
                <pre> {JSON.stringify(userError, null, 2)}</pre>
            </div>
        );
    }

    if (fetching || userLoading)
        return (
            <div>
                <h3>Loading</h3>
            </div>
        );

    let client = null;
    if (clientDashName) {
        client = dashNameLookup[clientDashName];
    }

    if (clientDashName && !client) {
        return (
            <div>
                <h3>No client with that name</h3>
            </div>
        );
    }

    const accessToShow = {
        performanceAnalysis: false,
        portfolioManager: false,
        portfolioRiskTradingLimits: false, // TODO: is there a special access control for risk tab?
        financialStatementsReporting: false,
        esg: false,
        operations: false
    };

    const frontendAssets: PermissionAssetEnum[] = userData.me.frontendRole.assets;

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Performance)) {
        accessToShow.performanceAnalysis = true;
    }

    if (
        frontendAssets.length === 0 ||
        frontendAssets.includes(PermissionAssetEnum.Position) ||
        frontendAssets.includes(PermissionAssetEnum.Limit)
    ) {
        accessToShow.portfolioRiskTradingLimits = true;
    }

    if (
        frontendAssets.length === 0 ||
        frontendAssets.includes(PermissionAssetEnum.Transaction) ||
        frontendAssets.includes(PermissionAssetEnum.Reconciliation)
    ) {
        accessToShow.operations = true;
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Accounting)) {
        if (
            frontendAssets.length === 0 ||
            (frontendAssets.includes(PermissionAssetEnum.Nav) &&
                frontendAssets.includes(PermissionAssetEnum.Position) &&
                frontendAssets.includes(PermissionAssetEnum.Transaction))
        ) {
            accessToShow.financialStatementsReporting = true;
        }
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.PortfolioManager)) {
        accessToShow.portfolioManager = true;
    }
    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Esg)) {
        accessToShow.esg = true;
    }

    return (
        <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap -mx-2 mb-5">
                <div className="px-2 mb-4">
                    <ClientContextSelector typeIn={[PartyType.Client]} defaultClientName={defaultClientName} />
                </div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <div className="flex flex-wrap justify-center">
                        {accessToShow.performanceAnalysis ? (
                            <div className="mx-1 my-1">
                                <Link to={clientDashName ? "/performance/" + clientDashName : "/performance"}>
                                    <div
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <ChartLine className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">Performance Analysis</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                        {accessToShow.portfolioRiskTradingLimits ? (
                            <div className="mx-1 my-1">
                                <Link to={clientDashName ? "/portfoliorisk/" + clientDashName : "/portfoliorisk"}>
                                    <div
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <CircleDollarSign className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">Portfolio Risk & Trading limits</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                        {accessToShow.portfolioManager ? (
                            <div className="mx-1 my-1">
                                <Link to={client && client._id !== emptyObjectId ? "/pm/" + client._id : "/pm"}>
                                    <div
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <Briefcase className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">Portfolio Management</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                        {accessToShow.financialStatementsReporting ? (
                            <div className="mx-1 my-1">
                                <Link to={client ? "/financialstatements/?clientId=" + client._id : "/financialstatements"}>
                                    <div
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <FileText className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">Financial Statements</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                        {accessToShow.esg ? (
                            <div className="mx-1 my-1">
                                <Link to={clientDashName ? "/esg/" + clientDashName : "/esg"}>
                                    <div
                                        id="/esg"
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <Leaf className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">ESG</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                        {accessToShow.operations ? (
                            <div className="mx-1 my-1">
                                <Link to={clientDashName ? "/operations/" + clientDashName : "/operations"}>
                                    <div
                                        onClick={null}
                                        style={{ height: "156px" }}
                                        className="w-80 cursor-pointer rounded-lg border border-sky-600/50 bg-sky-700 text-white shadow-sm transition-colors duration-200 hover:bg-sky-600 hover:shadow-lg"
                                    >
                                        <div className="px-4 py-3 h-full">
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <Settings className="w-24 h-20 text-white" />
                                                <div className="text-white text-center mt-2 text-lg">Operations</div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
